import {
  DotsThreeIcon,
  PencilSimpleLineIcon,
  ShareNetworkIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import IconButton from "@/components/ui/icon-button";
import {
  Menu,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { DEFAULT_NEW_CONVERSATION_TITLE } from "@/lib/utils/heraldChatUtils";

export interface ChatHistoryListItemProps {
  conversationId: string;
  title: string;
  isActive?: boolean;
  onSelect?: (conversationId: string) => void;
  onRename?: (conversationId: string) => void;
  onDelete?: (conversationId: string) => void;
  onShare?: (conversationId: string) => void;
  /** Keeps the overflow control visible (e.g. while rename/delete modal for this row is open). */
  pinOverflowMenu?: boolean;
  className?: string;
}

/**
 * Chat history row: title, hover-revealed overflow menu (rename / delete / optional share).
 */
const ChatHistoryListItem = ({
  conversationId,
  title,
  isActive = false,
  onSelect,
  onRename,
  onDelete,
  onShare,
  pinOverflowMenu = false,
  className,
}: ChatHistoryListItemProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const displayTitle = title.trim() || DEFAULT_NEW_CONVERSATION_TITLE;
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const titleSpanRef = useRef<HTMLSpanElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const updateTitleTruncation = useCallback(() => {
    const el = onSelect ? titleButtonRef.current : titleSpanRef.current;
    if (!el) return;
    setIsTitleTruncated(el.scrollWidth > el.clientWidth);
  }, [onSelect]);

  useLayoutEffect(() => {
    updateTitleTruncation();
    const el = onSelect ? titleButtonRef.current : titleSpanRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateTitleTruncation);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [displayTitle, onSelect, updateTitleTruncation]);

  /** Order: safe actions first, destructive last; separator renders only before Delete. */
  const menuItems = useMemo((): MenuItemConfig[] => {
    const items: MenuItemConfig[] = [];
    if (onRename) {
      items.push({
        id: "rename",
        icon: <PencilSimpleLineIcon className="text-gray-600" />,
        label: "Rename",
        onClick: () => onRename(conversationId),
      });
    }
    if (onShare) {
      items.push({
        id: "share",
        icon: <ShareNetworkIcon className="text-gray-600" />,
        label: "Share",
        onClick: () => onShare(conversationId),
      });
    }
    if (onDelete) {
      items.push({
        id: "delete",
        icon: <TrashIcon className="text-red-750" />,
        label: "Delete",
        onClick: () => onDelete(conversationId),
      });
    }
    return items;
  }, [conversationId, onRename, onDelete, onShare]);

  const hasMenu = menuItems.length > 0;

  /** Unstyled React Aria row label (same pattern as filter list items). */
  const titleClassName = cn(
    "block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left",
    "text-sm leading-5 font-normal text-inherit cursor-pointer",
  );

  const titleInner = onSelect ? (
    <Button
      ref={titleButtonRef}
      type="button"
      variant="none"
      size="label"
      onPress={() => onSelect(conversationId)}
      aria-label={`Open chat: ${displayTitle}`}
      className={cn(
        titleClassName,
        "border-0 bg-transparent p-0 data-hovered:bg-transparent data-pressed:bg-transparent",
      )}
    >
      {displayTitle}
    </Button>
  ) : (
    <span ref={titleSpanRef} className={cn(titleClassName, "cursor-pointer")}>
      {displayTitle}
    </span>
  );

  const titleBlock =
    isTitleTruncated && displayTitle.length > 0 ? (
      <TooltipTrigger delay={200}>
        {titleInner}
        <Tooltip align="left" placement="end" crossOffset={20} offset={-10}>
          {displayTitle}
        </Tooltip>
      </TooltipTrigger>
    ) : (
      titleInner
    );

  return (
    <li
      className={cn(
        "group flex w-full cursor-pointer items-center gap-1 overflow-hidden px-3 py-1.5 text-left text-sm leading-5 text-gray-800 transition-colors",

        isActive
          ? "bg-slate-300/10 hover:bg-slate-300/15"
          : "hover:bg-slate-300/10",

        className,

        "rounded-sm",
      )}
    >
      <div className="min-w-0 flex-1 overflow-hidden">{titleBlock}</div>
      {hasMenu ? (
        <div
          className={cn(
            "shrink-0 opacity-0 transition-opacity",
            "flex items-center justify-center",
            "group-hover:opacity-100",
            // Show when the trigger is keyboard-focused; avoid `focus-within` here — after the
            // menu closes, focus stays on the trigger and would keep the ⋯ stuck visible.
            "has-[button:focus-visible]:opacity-100",
            (pinOverflowMenu || isOverflowMenuOpen) && "opacity-100",
          )}
        >
          <MenuTrigger onOpenChange={setIsOverflowMenuOpen}>
            <IconButton
              icon={<DotsThreeIcon size={20} />}
              variant="ghost"
              aria-label={`More options for ${displayTitle}`}
              className="text-gray-600 h-5 p-0 data-hovered:bg-gray-200"
            />
            <MenuPopover placement="right top" crossOffset={10} offset={0}>
              <Menu>
                {menuItems.map((item) => (
                  <Fragment key={item.id}>
                    {item.id === "delete" && menuItems[0]?.id !== "delete" ? (
                      <MenuSeparator className="mx-1" />
                    ) : null}
                    <CustomMenuItem config={item} />
                  </Fragment>
                ))}
              </Menu>
            </MenuPopover>
          </MenuTrigger>
        </div>
      ) : null}
    </li>
  );
};

export default ChatHistoryListItem;
